/**
 * PrBulkReviewPage
 *
 * Admin review view for PR articles labelled by the AI text classifier. It
 * differs from the standard table pages in three ways:
 *   - a mutually-exclusive quick-filter toggle row (All / Needs Rereview /
 *     New Finds) plus a minimum PR-score slider, rendered above the table;
 *   - the table is locked to the dedicated "pr-bulk-review" view mode; and
 *   - the view selector offers only that one mode.
 *
 * The quick-filter and slider state live in the shared context (so they survive
 * navigation); this page composes the controls that drive them. The controls
 * component is page-local because it is used nowhere else.
 */

import { useEffect, useMemo, useState } from "react"
import { Info } from "lucide-react"
import { Slider } from "@/components/ui/slider"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import JournalArticlesShell from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
  PR_SCORE_SLIDER_MAX,
  PR_SCORE_SLIDER_STEP,
} from "@/components/journal-articles/useJournalArticlesPage"
import { PrBulkReviewFilter } from "@/components/journal-articles/JournalArticlesContext"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "PR Bulk Review"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string =
  "Review PR articles now labelled by our AI text classifier for all malaria articles published since 2000"

/**
 * The mutually-exclusive quick-filter toggles shown above the table. Exactly
 * one is active at a time (defaulting to "all"); the selected value drives the
 * server-side filters assembled in the shared context.
 */
const PR_BULK_REVIEW_FILTER_OPTIONS: {
  value: PrBulkReviewFilter
  label: string
  /** Optional explanation of the filter, surfaced via a hover info icon. */
  tooltip?: string
}[] = [
  { value: "all", label: "All" },
  {
    value: "needs-rereview",
    label: "Needs Rereview",
    tooltip:
      "Articles with a PR score above 0.5 that a reviewer previously marked as not covering the PR topic — worth a second look.",
  },
  {
    value: "new-finds",
    label: "New Finds",
    tooltip:
      "Articles with a PR score above 0.5 that have not yet been reviewed for the PR topic.",
  },
]

/**
 * Props for the page-local {@link PrBulkReviewControls}.
 */
interface PrBulkReviewControlsProps {
  /** The active quick-filter. */
  filter: PrBulkReviewFilter
  /** Change the active quick-filter (resets paging and the slider floor). */
  onFilterChange: (filter: PrBulkReviewFilter) => void
  /** The committed minimum PR score (the applied slider value). */
  scoreMin: number
  /** The lower bound of the slider for the active tab (0, or 0.5 on PR tabs). */
  scoreFloor: number
  /** Commit a new minimum PR score (fired on slider release). */
  onScoreCommit: (min: number) => void
}

/**
 * The PR Bulk Review control row: quick-filter toggles on the left, a minimum
 * PR-score slider on the right. Page-local — not a shared building block.
 *
 * @param props - The controls props.
 * @returns The rendered control row.
 */
function PrBulkReviewControls({
  filter,
  onFilterChange,
  scoreMin,
  scoreFloor,
  onScoreCommit,
}: PrBulkReviewControlsProps): React.JSX.Element {
  // Live slider value while dragging — drives the thumb and the ">" label for
  // immediate feedback. The committed value (which triggers a refetch) is only
  // pushed up on release via onValueCommit. Kept in sync with scoreMin whenever
  // that changes externally (tab switch clamp, URL seed, reset).
  const [sliderValue, setSliderValue] = useState<number>(scoreMin)
  useEffect(() => {
    setSliderValue(scoreMin)
  }, [scoreMin])

  return (
    <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
      {/* Left: mutually-exclusive quick-filter toggles. */}
      <div className="flex items-center gap-4" role="group" aria-label="PR Bulk Review filter">
        {PR_BULK_REVIEW_FILTER_OPTIONS.map(({ value, label, tooltip }) => (
          <div key={value} className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => onFilterChange(value)}
              aria-pressed={filter === value}
              className={`text-sm/3.5 h-8 inline-flex items-center gap-1.5 px-2.5 py-1.5 font-semibold border transition-all ${
                filter === value
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-border hover:bg-accent/40"
              }`}
            >
              <span className="text-muted-foreground">{label}</span>
            </button>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    // Purely informational; keep it out of the toggle so hovering
                    // / opening the tooltip never changes the filter.
                    aria-label={`What does "${label}" show?`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        ))}
      </div>

      {/* Right: minimum PR-score slider. Filters to scorePRTopic strictly
          greater than the chosen value; applied on release (onValueCommit). */}
      <div className="flex items-center gap-3 min-w-[260px]">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          PR score &gt; {sliderValue.toFixed(2)}
        </span>
        <Slider
          className="w-40 [&_[data-slot=slider-track]]:bg-yellow-400! [&_[data-slot=slider-range]]:bg-muted!"
          aria-label="Minimum PR score"
          min={scoreFloor}
          max={PR_SCORE_SLIDER_MAX}
          step={PR_SCORE_SLIDER_STEP}
          value={[sliderValue]}
          onValueChange={(values) => setSliderValue(values[0])}
          onValueCommit={(values) => onScoreCommit(values[0])}
        />
      </div>
    </div>
  )
}

/**
 * The PR Bulk Review page.
 *
 * @returns The rendered page element.
 */
export default function PrBulkReviewPage(): React.JSX.Element {
  const page = useJournalArticlesPage({
    sidebarOption: "pr-bulk-review",
    lockedViewMode: "pr-bulk-review",
    // Default to highest-PR-score-first. This is the most useful review order
    // and the order the backing sparse entity-scorePRTopic-index reads
    // natively, so the initial load takes the fast score-ordered query path.
    defaultSort: { key: "scorePRTopic", direction: "desc" },
  })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <JournalArticlesShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <PrBulkReviewControls
        filter={page.prBulkReviewFilter}
        onFilterChange={page.setPrBulkReviewFilter}
        scoreMin={page.prScoreMin}
        scoreFloor={page.prTabScoreFloor}
        onScoreCommit={page.setPrScoreMin}
      />
      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={visibleColumns}
        // PR Bulk Review locks the table to its single dedicated layout, so
        // only that option is offered in the view selector.
        viewModeOptions={["pr-bulk-review"]}
      />
    </JournalArticlesShell>
  )
}
